// ═══════════════════════════════════════════════════════════════
// VIDA — Voice Driven Initial Damage Assessment
// Mobile-first field capture: voice → AI → photos → submit
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconMicrophone, IconMicrophoneOff, IconCamera, IconTrash, IconMapPin, IconAlertTriangle } from '@tabler/icons-react';
import { useImpactStore } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import { WORKER_URL } from '../../lib/api-switch';
import { reverseGeocode } from './lib/gis';
import './VIDA.css';

// ── Types ──────────────────────────────────────────────────────

type Step = 'capture' | 'review' | 'photos' | 'success';

// ── Constants ──────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'structural',     label: 'Structural',     emoji: '🏚️' },
  { value: 'infrastructure', label: 'Infrastructure',  emoji: '🌉' },
  { value: 'vehicle',        label: 'Vehicle',         emoji: '🚗' },
  { value: 'utility',        label: 'Utility',         emoji: '⚡' },
  { value: 'other',          label: 'Other',           emoji: '📋' },
];

const SEVERITY_OPTIONS = [
  { value: 1, label: 'None',      bg: '#e8f5e9', color: '#037730' },
  { value: 2, label: 'Minor',     bg: '#fff8e1', color: '#885604' },
  { value: 3, label: 'Major',     bg: '#fff3e0', color: '#D97706' },
  { value: 5, label: 'Destroyed', bg: '#fdecea', color: '#D33423' },
];

const SEVERITY_MAP: Record<string, number> = {
  none: 1, minor: 2, major: 3, destroyed: 5,
};

// 'infrastructure' and 'utility' are public assets → PA type
const ASSESSMENT_TYPE: Record<string, 'individual' | 'public'> = {
  structural:     'individual',
  infrastructure: 'public',
  vehicle:        'individual',
  utility:        'public',
  other:          'individual',
};

// ── Helpers ────────────────────────────────────────────────────

function resizeImageToBase64(file: File, maxDim = 1024): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.8).split(',')[1]);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

// ── Component ──────────────────────────────────────────────────

export default function VIDA() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { createAssessment, uploadPhoto } = useImpactStore();

  // Step
  const [step, setStep] = useState<Step>('capture');

  // Voice
  const recognitionRef = useRef<any>(null);
  const [isRecording, setIsRecording]   = useState(false);
  const [transcript, setTranscript]     = useState('');
  const [interimText, setInterimText]   = useState('');
  const [speechSupported, setSpeechSupported] = useState(true);

  // GPS
  const [gpsStatus, setGpsStatus] = useState<'acquiring' | 'locked' | 'error'>('acquiring');
  const [coords, setCoords]   = useState<{ lat: number; lng: number } | null>(null);
  const [address, setAddress] = useState('');
  const geocodedRef = useRef(false);

  // Review
  const [description, setDescription] = useState('');
  const [category, setCategory]       = useState('structural');
  const [severity, setSeverity]       = useState(2);
  const [aiProcessing, setAiProcessing] = useState(false);

  // Photos
  const [photos, setPhotos]           = useState<File[]>([]);
  const [photoThumbs, setPhotoThumbs] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState<number | null>(null);

  // ── GPS: start watching on mount ──────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation) { setGpsStatus('error'); return; }
    const watchId = navigator.geolocation.watchPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng });
        setGpsStatus('locked');
        if (!geocodedRef.current) {
          geocodedRef.current = true;
          try {
            const result = await reverseGeocode(lat, lng);
            if (result?.address) setAddress(result.address);
          } catch { /* ignore */ }
        }
      },
      () => setGpsStatus('error'),
      { enableHighAccuracy: true, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // ── Check speech API support ──────────────────────────────────
  useEffect(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) setSpeechSupported(false);
  }, []);

  // ── Voice: start ──────────────────────────────────────────────
  const startRecording = useCallback(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.continuous     = true;
    recognition.interimResults = true;
    recognition.lang           = 'en-US';
    recognition.onresult = (event: any) => {
      let interim = '';
      let final   = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) final += event.results[i][0].transcript + ' ';
        else interim += event.results[i][0].transcript;
      }
      if (final) setTranscript(prev => prev + final);
      setInterimText(interim);
    };
    recognition.onerror = () => { setIsRecording(false); setInterimText(''); };
    recognition.onend   = () => { setIsRecording(false); setInterimText(''); };
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  }, []);

  const stopRecording = useCallback(() => {
    recognitionRef.current?.stop();
    setIsRecording(false);
    setInterimText('');
  }, []);

  // ── AI processing ─────────────────────────────────────────────
  const proceedToReview = async () => {
    const rawText = transcript.trim();
    if (!rawText) { setStep('review'); return; }
    setAiProcessing(true);
    try {
      const res = await fetch(`${WORKER_URL}/api/claude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 400,
          messages: [{
            role: 'user',
            content: `You are analyzing a voice transcript from a field damage assessment worker. Clean it up and extract structured data.

Transcript: "${rawText}"

Return ONLY valid JSON, no markdown:
{
  "description": "clean 1-3 sentence description of the damage observed",
  "category": "structural" or "infrastructure" or "vehicle" or "utility" or "other",
  "severity": "none" or "minor" or "major" or "destroyed",
  "mentionedAddress": "any specific address or location mentioned in the transcript, or null"
}`,
          }],
        }),
      });
      const data = await res.json();
      const text = (data.content?.[0]?.text || '')
        .replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(text);
      setDescription(parsed.description || rawText);
      if (CATEGORIES.find(c => c.value === parsed.category)) setCategory(parsed.category);
      if (SEVERITY_MAP[parsed.severity]) setSeverity(SEVERITY_MAP[parsed.severity]);
      if (parsed.mentionedAddress && !address) setAddress(parsed.mentionedAddress);
    } catch {
      setDescription(rawText); // fall back to raw transcript
    } finally {
      setAiProcessing(false);
      setStep('review');
    }
  };

  // ── Photos ────────────────────────────────────────────────────
  const handlePhotoAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    const thumbs = await Promise.all(
      files.map(f => new Promise<string>(resolve => {
        const reader = new FileReader();
        reader.onload = ev => resolve(ev.target?.result as string);
        reader.readAsDataURL(f);
      }))
    );
    setPhotos(prev => [...prev, ...files]);
    setPhotoThumbs(prev => [...prev, ...thumbs]);
    e.target.value = '';
  };

  const removePhoto = (i: number) => {
    setPhotos(prev => prev.filter((_, idx) => idx !== i));
    setPhotoThumbs(prev => prev.filter((_, idx) => idx !== i));
  };

  // ── Submit ────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!activeIncident) return;
    setSubmitting(true);
    try {
      const id = await createAssessment({
        incidentId:     String(activeIncident.id),
        assessmentType: ASSESSMENT_TYPE[category] ?? 'individual',
        damageCategory: category,
        paCategory:     '',
        severity,
        address:        address || '',
        county:         '',
        fipsCode:       '',
        stateCode:      '',
        latitude:       coords?.lat ?? null,
        longitude:      coords?.lng ?? null,
        damageTypes:    [],
        description,
        method:         'vida',
        assessorName:   user?.fullName || '',
        assessorEmail:  user?.email    || '',
        facilityName:   '',
        residenceType:  '',
        ownership:      '',
        dwellingType:   '',
      });
      if (id && photos.length > 0) {
        for (let i = 0; i < photos.length; i++) {
          try {
            const b64 = await resizeImageToBase64(photos[i]);
            await uploadPhoto(id, b64, i);
          } catch { /* non-blocking */ }
        }
      }
      setSavedId(id);
      setStep('success');
    } catch {
      alert('Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep('capture');
    setTranscript(''); setInterimText('');
    setDescription(''); setCategory('structural'); setSeverity(2);
    setPhotos([]); setPhotoThumbs([]);
    setSavedId(null);
  };

  // ── No incident guard ─────────────────────────────────────────
  if (!activeIncident) {
    return (
      <div className="vida-empty">
        <div className="vida-empty-icon">⚠️</div>
        <div className="vida-empty-title">No Active Incident</div>
        <div className="vida-empty-sub">Select an incident to start a VIDA assessment.</div>
        <button className="vida-btn-primary" onClick={() => navigate('/incidents')}>
          Go to Incidents
        </button>
      </div>
    );
  }

  const stepIdx = ['capture', 'review', 'photos', 'success'].indexOf(step);

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="vida-wrap">

      {/* Header */}
      {step !== 'success' && (
        <div className="vida-header">
          <div className="vida-title">
            <span className="vida-logo">VIDA</span>
            <span className="vida-subtitle">Voice Damage Assessment</span>
          </div>
          <div className="vida-steps">
            {[0, 1, 2].map(i => (
              <div key={i} className={`vida-dot ${i < stepIdx ? 'done' : i === stepIdx ? 'active' : ''}`} />
            ))}
          </div>
        </div>
      )}

      {/* GPS bar */}
      {step !== 'success' && (
        <div className={`vida-gps-bar ${gpsStatus}`}>
          <IconMapPin size={14} />
          {gpsStatus === 'acquiring' && <span>Acquiring GPS…</span>}
          {gpsStatus === 'locked'    && <span>{address || `${coords?.lat.toFixed(5)}, ${coords?.lng.toFixed(5)}`}</span>}
          {gpsStatus === 'error'     && <span>GPS unavailable — enter address in Step 2</span>}
        </div>
      )}

      {/* ── STEP 1: CAPTURE ── */}
      {step === 'capture' && (
        <div className="vida-step">
          <div className="vida-step-label">Step 1 — Describe what you see</div>

          <div className="vida-transcript">
            {transcript || interimText ? (
              <>
                <span className="vida-transcript-final">{transcript}</span>
                <span className="vida-transcript-interim">{interimText}</span>
              </>
            ) : (
              <span className="vida-transcript-placeholder">
                {isRecording ? 'Listening…' : 'Tap the button and describe the damage'}
              </span>
            )}
          </div>

          {speechSupported ? (
            <div className="vida-record-wrap">
              {isRecording && <div className="vida-pulse-ring" />}
              <button
                className={`vida-record-btn ${isRecording ? 'recording' : ''}`}
                onClick={() => isRecording ? stopRecording() : startRecording()}
              >
                {isRecording ? <IconMicrophoneOff size={52} /> : <IconMicrophone size={52} />}
                <span className="vida-record-label">
                  {isRecording ? 'TAP TO STOP' : 'TAP TO SPEAK'}
                </span>
              </button>
            </div>
          ) : (
            <div className="vida-no-speech">
              <IconAlertTriangle size={20} />
              <span>Voice input not supported on this device — type your description below.</span>
            </div>
          )}

          {/* Manual text entry — shown as fallback or to edit */}
          {(!speechSupported || transcript) && (
            <textarea
              className="vida-textarea"
              placeholder="Type your damage description…"
              value={transcript}
              onChange={e => setTranscript(e.target.value)}
              rows={4}
            />
          )}

          <div className="vida-actions">
            <button className="vida-btn-ghost" onClick={() => { setTranscript(''); proceedToReview(); }}>
              Skip
            </button>
            <button
              className="vida-btn-primary"
              onClick={proceedToReview}
              disabled={aiProcessing}
            >
              {aiProcessing ? 'Processing…' : 'Next →'}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: REVIEW ── */}
      {step === 'review' && (
        <div className="vida-step">
          <div className="vida-step-label">Step 2 — Confirm the details</div>

          <div>
            <div className="vida-field-label">Description</div>
            <textarea
              className="vida-textarea"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe the damage…"
              rows={4}
            />
          </div>

          <div>
            <div className="vida-field-label">Damage Type</div>
            <div className="vida-category-grid">
              {CATEGORIES.map(c => (
                <button
                  key={c.value}
                  className={`vida-category-btn ${category === c.value ? 'selected' : ''}`}
                  onClick={() => setCategory(c.value)}
                >
                  <span className="vida-category-emoji">{c.emoji}</span>
                  <span className="vida-category-label">{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="vida-field-label">Severity</div>
            <div className="vida-severity-row">
              {SEVERITY_OPTIONS.map(s => (
                <button
                  key={s.value}
                  className="vida-severity-btn"
                  style={{
                    background:  severity === s.value ? s.bg    : '#f5f5f5',
                    color:       severity === s.value ? s.color : '#666',
                    border:      `2px solid ${severity === s.value ? s.color : 'transparent'}`,
                    fontWeight:  severity === s.value ? 700 : 400,
                  }}
                  onClick={() => setSeverity(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="vida-field-label">Location</div>
            <input
              className="vida-input"
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="Address or location description"
            />
          </div>

          <div className="vida-actions">
            <button className="vida-btn-ghost" onClick={() => setStep('capture')}>← Back</button>
            <button className="vida-btn-primary" onClick={() => setStep('photos')}>Add Photos →</button>
          </div>
        </div>
      )}

      {/* ── STEP 3: PHOTOS ── */}
      {step === 'photos' && (
        <div className="vida-step">
          <div className="vida-step-label">Step 3 — Add photos (optional)</div>

          {photoThumbs.length > 0 && (
            <div className="vida-photo-grid">
              {photoThumbs.map((thumb, i) => (
                <div key={i} className="vida-photo-thumb">
                  <img src={thumb} alt={`Photo ${i + 1}`} />
                  <button className="vida-photo-remove" onClick={() => removePhoto(i)}>
                    <IconTrash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {photos.length < 5 && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                style={{ display: 'none' }}
                onChange={handlePhotoAdd}
              />
              <button className="vida-camera-btn" onClick={() => fileInputRef.current?.click()}>
                <IconCamera size={40} />
                <span>{photos.length === 0 ? 'Tap to Take or Upload Photos' : 'Add Another Photo'}</span>
                <span className="vida-camera-sub">{photos.length} / 5 photos added</span>
              </button>
            </>
          )}

          <div className="vida-actions">
            <button className="vida-btn-ghost" onClick={() => setStep('review')}>← Back</button>
            <button className="vida-btn-submit" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving…' : '✓ Submit'}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: SUCCESS ── */}
      {step === 'success' && (
        <div className="vida-success">
          <div className="vida-success-icon">✅</div>
          <div className="vida-success-title">Assessment Saved!</div>
          {savedId && <div className="vida-success-id">Record #{savedId}</div>}
          <div className="vida-success-actions">
            <button className="vida-btn-primary" onClick={resetForm}>
              Submit Another
            </button>
            <button className="vida-btn-ghost vida-btn-full" onClick={() => navigate('/impact/assessments')}>
              View All Assessments
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
